/**
 * Container data + actions for the Structural Extraction modal. Owns ALL
 * imperative / IO concerns:
 *   - Parquet preview loading + paginated query/filter (lakeApi)
 *   - the rectangular row/column selection + header config state
 *   - the cases list + scope save in create mode (extractionScopeApi.create)
 *   - the saved-scope fetch + restore in readOnly mode (extractionScopeApi.getByCase)
 *   - the inline schema builder graph (entities/relationships, ReactFlow nodes
 *     & edges, dagre auto-layout, the visual column/value picker)
 *   - schema save + mark-as-reviewed (extractionScopeApi.updateSchema, casesApi)
 *
 * so `StructuralExtractionModal` can stay a thin presentational orchestrator.
 */

import Dagre from '@dagrejs/dagre'
import { type Edge, MarkerType, type Node, useEdgesState, useNodesState } from '@xyflow/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { pickColor } from '@/components/lake/extraction-modal/helpers'
import type {
  BuiltEntity,
  BuiltRelationship,
  PickingContext,
  SelectionMode,
} from '@/components/lake/extraction-modal/types'
import { toast } from '@/components/ui/sonner'
import { caseDocumentImportApi } from '@/services/api/case-document-import'
import type { RowCondition } from '@/services/api/cases'
import { type Case, casesApi } from '@/services/api/cases'
import { type ExtractionScope, extractionScopeApi } from '@/services/api/extraction-scope'
import type { LakeDocument, TabularColumn, TabularQueryRequest } from '@/services/api/lake'
import { lakeApi } from '@/services/api/lake'
import type { Schema } from '@/services/api/schemas'
import { schemasApi } from '@/services/api/schemas'
import { buildWizardConfig, validateSchemaGraph } from '../utils/buildWizardConfig'

type TabularRow = (string | number | boolean | null)[]

export interface UseStructuralExtractionArgs {
  document: LakeDocument
  open: boolean
  onClose: () => void
  onSaved: () => void
  readOnly: boolean
  preSelectedCaseId?: string
  isDark: boolean
}

export interface UseStructuralExtractionResult {
  // preview data
  columns: TabularColumn[]
  rows: TabularRow[]
  totalRows: number
  isLoading: boolean
  isNavigating: boolean
  currentOffset: number
  queryError: string | null
  dataCols: TabularColumn[]
  hasParquet: boolean
  // selection
  selectionMode: SelectionMode
  setSelectionMode: (mode: SelectionMode) => void
  headerSource: 'file' | 'manual'
  setHeaderSource: (source: 'file' | 'manual') => void
  headerRowIndex: number | null
  setHeaderRowIndex: (index: number | null) => void
  headerNames: string[]
  setHeaderNames: (names: string[]) => void
  pendingRows: Set<number>
  pendingColumns: Set<number>
  clearSelection: () => void
  // create-mode scope save
  cases: Case[]
  selectedCaseId: string
  setSelectedCaseId: (id: string) => void
  scopeNote: string
  setScopeNote: (note: string) => void
  saving: boolean
  canSave: boolean
  noHeaders: boolean
  emptyHeaders: boolean
  noSelection: boolean
  noCaseSelected: boolean
  handleSave: () => Promise<void>
  // readOnly display
  readOnlyCaseName: string
  scopeLoadError: string | null
  loadedScope: ExtractionScope | null
  setLoadedScope: React.Dispatch<React.SetStateAction<ExtractionScope | null>>
  isGeospatial: boolean
  // mark reviewed
  marking: boolean
  handleSaveAndMarkReviewed: () => Promise<void>
  // split / sidebar layout
  splitPercent: number
  handleDragStart: (e: React.MouseEvent<HTMLDivElement>) => void
  sidebarWidth: number
  handleSidebarDragStart: (e: React.MouseEvent<HTMLDivElement>) => void
  // schema builder
  schema: Schema | null
  loadingSchema: boolean
  entities: BuiltEntity[]
  relationships: BuiltRelationship[]
  selectedEntityId: string | null
  setSelectedEntityId: (id: string | null) => void
  selectedRelationshipId: string | null
  rightPanelVisible: boolean
  setRightPanelVisible: (visible: boolean) => void
  availableColumns: string[]
  tabularData: { columns: TabularColumn[]; rows: TabularRow[] } | null
  nodes: Node[]
  edges: Edge[]
  onNodesChange: ReturnType<typeof useNodesState>[2]
  onEdgesChange: ReturnType<typeof useEdgesState>[2]
  handleAddEntity: (
    entityType: string,
    label: string,
    identifierColumn: string | null,
    propertyMappings: Record<string, string>,
    rowCondition: RowCondition | null,
    isAnchor?: boolean
  ) => void
  handleDeleteEntity: (entityId: string) => void
  handleCreateRelationship: (sourceEntityId: string, targetEntityId: string) => void
  handleUpdateRelationship: (
    relationshipId: string,
    relationshipType: string,
    propertyMappings: Record<string, string>
  ) => void
  handleDeleteRelationship: (relationshipId: string) => void
  handleNodeClick: (e: React.MouseEvent, node: Node) => void
  handleEdgeClick: (e: React.MouseEvent, edge: Edge) => void
  handlePropertyMapping: (propertyName: string, columnName: string | null) => void
  handleSetIdentifier: (columnName: string | null) => void
  handleAutoLayout: () => void
  // visual picking
  pickingContext: PickingContext
  handleStartPickingColumn: (propertyName: string) => void
  handleStartPickingRowConditionColumn: (clauseIndex: number) => void
  handleStartPickingRowConditionValue: (clauseIndex: number) => void
  handleColumnPicked: (columnName: string) => void
  handleCellPicked: (columnName: string, cellValue: string | number | boolean | null) => void
  // refs shared with SchemaTab
  saveCurrentEntityRef: React.MutableRefObject<(() => boolean) | null>
  updateLocalPropertyMappingRef: React.MutableRefObject<
    ((propertyName: string, columnName: string) => void) | null
  >
  updateLocalRowConditionRef: React.MutableRefObject<
    ((clauseIndex: number, field: 'column' | 'value', newValue: string) => void) | null
  >
  tableContainerRef: React.RefObject<HTMLDivElement | null>
  // query / paging / cell interaction
  handleQuery: (req: TabularQueryRequest) => void
  handlePrevPage: () => void
  handleNextPage: () => void
  handleRowClick: (rowIndex: number) => void
  handleColumnClick: (colIdx: number) => void
  handleDragSelect: (dragRows: number[], dragCols: number[]) => void
}

export function useStructuralExtraction({
  document,
  open,
  onClose,
  onSaved,
  readOnly,
  preSelectedCaseId,
  isDark,
}: UseStructuralExtractionArgs): UseStructuralExtractionResult {
  // ---- preview data state ----
  const [columns, setColumns] = useState<TabularColumn[]>([])
  const [rows, setRows] = useState<TabularRow[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const [currentOffset, setCurrentOffset] = useState(0)
  const [queryError, setQueryError] = useState<string | null>(null)
  const [currentQuery, setCurrentQuery] = useState<TabularQueryRequest>({})

  // ---- selection state ----
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('build-batch')
  const [headerSource, setHeaderSource] = useState<'file' | 'manual'>('file')
  const [headerRowIndex, setHeaderRowIndex] = useState<number | null>(null)
  const [headerNames, setHeaderNames] = useState<string[]>([])
  const [pendingRows, setPendingRows] = useState<Set<number>>(new Set())
  const [pendingColumns, setPendingColumns] = useState<Set<number>>(new Set())

  // ---- scope save / display state ----
  const [cases, setCases] = useState<Case[]>([])
  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  const [scopeNote, setScopeNote] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [readOnlyCaseName, setReadOnlyCaseName] = useState<string>('')
  const [scopeLoadError, setScopeLoadError] = useState<string | null>(null)
  const [loadedScope, setLoadedScope] = useState<ExtractionScope | null>(null)

  const isGeospatial = loadedScope?.manual_schema?.data_pattern === 'geospatial'

  // ---- mark reviewed ----
  const [marking, setMarking] = useState(false)

  // ---- layout state ----
  const [splitPercent, setSplitPercent] = useState(55)
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const defaultWidth = window.innerWidth * 0.3
    return Math.min(600, Math.max(320, defaultWidth))
  })

  // ---- schema builder state ----
  const [schema, setSchema] = useState<Schema | null>(null)
  const [loadingSchema, setLoadingSchema] = useState(false)
  const [entities, setEntities] = useState<BuiltEntity[]>([])
  const [relationships, setRelationships] = useState<BuiltRelationship[]>([])
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null)
  const [selectedRelationshipId, setSelectedRelationshipId] = useState<string | null>(null)
  const [rightPanelVisible, setRightPanelVisible] = useState(false)
  const [tabularData, setTabularData] = useState<{
    columns: TabularColumn[]
    rows: TabularRow[]
  } | null>(null)
  const [, setLoadingTabularData] = useState(false)

  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  const [pickingContext, setPickingContext] = useState<PickingContext>(null)

  const saveCurrentEntityRef = useRef<(() => boolean) | null>(null)
  const updateLocalPropertyMappingRef = useRef<
    ((propertyName: string, columnName: string) => void) | null
  >(null)
  const updateLocalRowConditionRef = useRef<
    ((clauseIndex: number, field: 'column' | 'value', newValue: string) => void) | null
  >(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  // ---- derived ----
  const dataCols = columns.filter(c => c.name !== '__row_index')
  const hasParquet = !!document.has_parquet

  const availableColumns = useMemo(() => {
    // New tabular imports store the selected column names directly.
    if (loadedScope?.selected_columns?.length) {
      return loadedScope.selected_columns
    }
    if (loadedScope?.tabular_config) {
      const batch = loadedScope.tabular_config.batches[0]
      if (batch) {
        return batch.columns
          .map(i => loadedScope.tabular_config?.headers[i])
          .filter((h): h is string => Boolean(h))
      }
    }
    if (tabularData?.columns) {
      return tabularData.columns.map(col => col.name)
    }
    return []
  }, [loadedScope, tabularData])

  // ---- load on open ----
  useEffect(() => {
    if (!open) return

    setColumns([])
    setRows([])
    setTotalRows(0)
    setCurrentOffset(0)
    setQueryError(null)
    setCurrentQuery({})
    setSelectionMode('build-batch')
    setHeaderRowIndex(null)
    setHeaderNames([])
    setPendingRows(new Set())
    setPendingColumns(new Set())
    setScopeNote('')
    setReadOnlyCaseName('')
    setScopeLoadError(null)
    setLoadedScope(null)
    setEntities([])
    setRelationships([])
    setSelectedEntityId(null)
    setRightPanelVisible(false)
    setSplitPercent(55)

    if (readOnly) {
      if (preSelectedCaseId && hasParquet) {
        setIsLoading(true)

        // Load the saved scope and Parquet preview together — restoring the
        // selection needs BOTH: the scope stores column NAMES, which map to
        // data-column indices via the preview's columns so the right cells
        // highlight.
        Promise.all([
          extractionScopeApi.getByCase(document.id, preSelectedCaseId).catch(() => null),
          lakeApi.getTabularPreview(document.id, 0, 250).catch(() => null),
        ])
          .then(([scope, resp]) => {
            if (resp) {
              setColumns(resp.columns)
              setRows(resp.rows)
              setTotalRows(resp.total_rows)
            } else {
              setQueryError('Failed to load preview')
            }

            const hasTabular = !!(
              scope &&
              (scope.tabular_config ||
                scope.selected_columns?.length ||
                scope.selected_rows?.length)
            )
            if (!scope || (!hasTabular && !scope.manual_schema)) {
              setScopeLoadError('No tabular scope found for this case.')
              return
            }
            setLoadedScope(scope)

            if (scope.selected_columns?.length || scope.selected_rows?.length) {
              // New tabular import: map stored column NAMES → data-column
              // indices and restore the saved __row_index selection.
              const previewDataCols = (resp?.columns ?? []).filter(c => c.name !== '__row_index')
              const nameToIndex = new Map(previewDataCols.map((c, i) => [c.name, i]))
              const columnIdxs = (scope.selected_columns ?? [])
                .map(name => nameToIndex.get(name))
                .filter((i): i is number => i !== undefined)
              setHeaderNames(previewDataCols.map(c => c.name))
              setHeaderRowIndex(null)
              setPendingColumns(new Set(columnIdxs))
              setPendingRows(new Set(scope.selected_rows ?? []))
            } else if (scope.tabular_config) {
              const cfg = scope.tabular_config
              setHeaderNames(cfg.headers)
              setHeaderRowIndex(cfg.header_row_index)
              if (cfg.batches.length > 0) {
                setPendingRows(new Set(cfg.batches[0].rows))
                setPendingColumns(new Set(cfg.batches[0].columns))
              }
            }
            setScopeNote(scope.scope_note ?? '')
            setReadOnlyCaseName(scope.case_name)
          })
          .finally(() => setIsLoading(false))
      }
    } else {
      casesApi
        .list()
        .then(setCases)
        .catch(() => setCases([]))

      if (!hasParquet) return
      setIsLoading(true)
      lakeApi
        .getTabularPreview(document.id, 0, 250)
        .then(resp => {
          setColumns(resp.columns)
          setRows(resp.rows)
          setTotalRows(resp.total_rows)
          const dc = resp.columns.filter(c => c.name !== '__row_index')
          setHeaderNames(dc.map(c => c.name))
        })
        .catch((err: unknown) => {
          setQueryError(err instanceof Error ? err.message : 'Failed to load preview')
        })
        .finally(() => setIsLoading(false))
    }
  }, [open, document.id, hasParquet, readOnly, preSelectedCaseId])

  // ---- load active schema ----
  useEffect(() => {
    if (!open) return
    setLoadingSchema(true)
    schemasApi
      .getActive()
      .then(activeSchema => setSchema(activeSchema))
      .catch((err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status !== 404) {
          toast.error('Failed to load extraction schema')
        }
      })
      .finally(() => setLoadingSchema(false))
  }, [open])

  // ---- load tabular preview for column samples ----
  useEffect(() => {
    if (!open || !document.id) return
    setLoadingTabularData(true)
    lakeApi
      .getTabularPreview(document.id, 0, 50)
      .then(resp => {
        setTabularData({ columns: resp.columns, rows: resp.rows })
      })
      .catch(() => {
        // Non-blocking - continue without previews
      })
      .finally(() => setLoadingTabularData(false))
  }, [open, document.id])

  // ---- initialize entities/relationships from manual_schema ----
  const [needsAutoLayout, setNeedsAutoLayout] = useState(false)

  useEffect(() => {
    if (!open || !loadedScope?.manual_schema) return

    const ms = loadedScope.manual_schema

    const savedPositions = new Map<string, { x: number; y: number }>()
    if (ms.graph_nodes) {
      for (const gn of ms.graph_nodes) {
        if (gn.x !== undefined && gn.y !== undefined) {
          savedPositions.set(gn.label, { x: gn.x, y: gn.y })
        }
      }
    }

    const builtEntities: BuiltEntity[] = ms.entities.map((e, i) => {
      const pos = savedPositions.get(e.label)
      return {
        id: crypto.randomUUID(),
        entityType: e.entity_type,
        label: e.label,
        color: e.color || pickColor(i),
        columnMappings: e.properties_mapping || {},
        identifierColumn: e.identifier_column || null,
        rowCondition: e.row_condition ?? null,
        isAnchor: ms.graph_nodes?.find(gn => gn.label === e.label)?.is_anchor,
        x: pos?.x,
        y: pos?.y,
      }
    })
    setEntities(builtEntities)

    const hasAnyPosition = builtEntities.some(e => e.x !== undefined && e.y !== undefined)
    setNeedsAutoLayout(!hasAnyPosition && builtEntities.length > 0)

    const builtRels: BuiltRelationship[] = ms.relationships.map((r, i) => {
      const sourceEnt = builtEntities.find(e => e.label === r.source_entity)
      const targetEnt = builtEntities.find(e => e.label === r.target_entity)
      return {
        id: crypto.randomUUID(),
        relationshipType: r.relationship_type,
        sourceEntityId: sourceEnt?.id || '',
        targetEntityId: targetEnt?.id || '',
        color: r.color || pickColor(i),
        columnMappings: r.properties_mapping || {},
      }
    })
    setRelationships(builtRels)
  }, [open, loadedScope?.manual_schema, loadedScope])

  // ---- build ReactFlow nodes from entities ----
  const reactFlowNodes = useMemo(() => {
    return entities.map((ent, i) => ({
      id: ent.id,
      type: 'entityNode' as const,
      position: {
        x: ent.x ?? 50 + (i % 3) * 250,
        y: ent.y ?? 50 + Math.floor(i / 3) * 180,
      },
      data: {
        entityId: ent.id,
        entityType: ent.entityType,
        entityLabel: ent.label,
        color: ent.color,
        isConditional: ent.rowCondition !== null,
        isAnchor: ent.isAnchor,
      },
    }))
  }, [entities])

  useEffect(() => {
    setNodes(currentNodes => {
      const currentNodeMap = new Map(currentNodes.map(n => [n.id, n]))
      return reactFlowNodes.map(newNode => {
        const existingNode = currentNodeMap.get(newNode.id)
        if (existingNode) {
          return { ...newNode, position: existingNode.position }
        }
        return newNode
      })
    })
  }, [reactFlowNodes, setNodes])

  const reactFlowEdges = useMemo(() => {
    return relationships.map(rel => {
      const isSelected = selectedRelationshipId === rel.id
      return {
        id: rel.id,
        source: rel.sourceEntityId,
        target: rel.targetEntityId,
        label: `${rel.relationshipType} →`,
        type: 'smoothstep' as const,
        animated: isSelected,
        interactionWidth: 20,
        style: {
          stroke: rel.color,
          strokeWidth: isSelected ? 4 : 2,
          cursor: 'pointer',
        },
        labelStyle: {
          fill: isSelected ? (isDark ? '#ffffff' : '#1e293b') : isDark ? '#9ca3af' : '#64748b',
          fontSize: isSelected ? 12 : 10,
          fontWeight: isSelected ? 'bold' : 'normal',
        },
        labelBgStyle: {
          fill: isDark ? '#171717' : '#f1f5f9',
          fillOpacity: 0.9,
        },
        labelBgPadding: [4, 2] as [number, number],
        markerStart: {
          type: MarkerType.Arrow,
          color: rel.color,
          width: 12,
          height: 12,
          strokeWidth: 2,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: rel.color,
          width: isSelected ? 25 : 20,
          height: isSelected ? 25 : 20,
        },
      }
    })
  }, [relationships, selectedRelationshipId, isDark])

  useEffect(() => {
    setEdges(reactFlowEdges)
  }, [reactFlowEdges, setEdges])

  // ---- auto-layout via dagre ----
  const getLayoutedElements = useCallback(
    (nodesToLayout: Node[], edgesToLayout: Edge[], direction: 'LR' | 'TB' = 'LR') => {
      const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}))
      g.setGraph({ rankdir: direction, nodesep: 80, ranksep: 120 })

      for (const node of nodesToLayout) {
        g.setNode(node.id, { width: 160, height: 80 })
      }
      for (const edge of edgesToLayout) {
        g.setEdge(edge.source, edge.target)
      }

      Dagre.layout(g)

      return nodesToLayout.map(node => {
        const pos = g.node(node.id)
        return {
          ...node,
          position: { x: pos.x - 80, y: pos.y - 40 },
        }
      })
    },
    []
  )

  const handleAutoLayout = useCallback(() => {
    const layoutedNodes = getLayoutedElements(nodes, edges, 'LR')
    setNodes(layoutedNodes)
  }, [nodes, edges, setNodes, getLayoutedElements])

  useEffect(() => {
    if (needsAutoLayout && nodes.length > 0 && edges.length >= 0) {
      const timer = setTimeout(() => {
        handleAutoLayout()
        setNeedsAutoLayout(false)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [needsAutoLayout, nodes.length, edges.length, handleAutoLayout])

  useEffect(() => {
    if (selectedEntityId) {
      setRightPanelVisible(true)
    }
  }, [selectedEntityId])

  // ---- mark as reviewed (readOnly) ----
  const handleSaveAndMarkReviewed = useCallback(async () => {
    if (!loadedScope || !preSelectedCaseId) return

    if (isGeospatial && loadedScope.manual_schema) {
      setMarking(true)
      try {
        const updatedScope = await extractionScopeApi.updateSchema(
          document.id,
          loadedScope.id,
          loadedScope.manual_schema
        )
        await casesApi.markSchemaReviewed(preSelectedCaseId, document.id)
        setLoadedScope(updatedScope)
        toast.success('Geo mapping saved and marked as reviewed')
        onSaved()
      } catch (err: unknown) {
        toast.error(err instanceof Error ? err.message : 'Failed to save')
      } finally {
        setMarking(false)
      }
      return
    }

    const validationError = validateSchemaGraph(entities, relationships)
    if (validationError) {
      toast.error(validationError)
      return
    }

    setMarking(true)
    try {
      const nodePositions = new Map(nodes.map(n => [n.id, n.position]))
      const wizardConfig = buildWizardConfig({
        entities,
        relationships,
        availableColumns,
        nodePositions,
      })

      const updatedScope = await extractionScopeApi.updateSchema(
        document.id,
        loadedScope.id,
        wizardConfig
      )
      await casesApi.markSchemaReviewed(preSelectedCaseId, document.id)

      setLoadedScope(updatedScope)
      toast.success('Schema saved and marked as reviewed')
      onSaved()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to save and mark as reviewed')
    } finally {
      setMarking(false)
    }
  }, [
    loadedScope,
    preSelectedCaseId,
    isGeospatial,
    document.id,
    entities,
    relationships,
    availableColumns,
    nodes,
    onSaved,
  ])

  // ---- split pane / sidebar drag handles ----
  const handleDragStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      const container = e.currentTarget.closest('[data-split-container]') as HTMLElement
      const startY = e.clientY
      const startPct = splitPercent
      function onMove(me: MouseEvent) {
        const totalH = container.getBoundingClientRect().height
        const delta = ((me.clientY - startY) / totalH) * 100
        setSplitPercent(Math.min(80, Math.max(20, startPct + delta)))
      }
      function onUp() {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [splitPercent]
  )

  const handleSidebarDragStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      const startX = e.clientX
      const startWidth = sidebarWidth

      function onMove(me: MouseEvent) {
        const delta = me.clientX - startX
        const newWidth = startWidth + delta
        setSidebarWidth(Math.min(600, Math.max(320, newWidth)))
      }
      function onUp() {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [sidebarWidth]
  )

  // ---- schema builder handlers ----
  const handleAddEntity = useCallback(
    (
      entityType: string,
      label: string,
      identifierColumn: string | null,
      propertyMappings: Record<string, string>,
      rowCondition: RowCondition | null,
      isAnchor?: boolean
    ) => {
      if (selectedEntityId) {
        setEntities(prev =>
          prev.map(e =>
            e.id === selectedEntityId
              ? {
                  ...e,
                  entityType,
                  label,
                  columnMappings: propertyMappings,
                  identifierColumn,
                  rowCondition,
                  isAnchor: isAnchor !== undefined ? isAnchor : e.isAnchor,
                }
              : e
          )
        )
        return
      }

      const newEntity: BuiltEntity = {
        id: crypto.randomUUID(),
        entityType,
        label,
        color: pickColor(entities.length),
        columnMappings: propertyMappings,
        identifierColumn,
        rowCondition,
        isAnchor,
      }
      setEntities(prev => [...prev, newEntity])

      if (label !== 'New Entity' && label !== 'Anchor Node') {
        toast.success(`Added entity: ${label}`)
      }
      setSelectedEntityId(newEntity.id)
      setRightPanelVisible(true)
    },
    [entities.length, selectedEntityId]
  )

  const handleDeleteEntity = useCallback((entityId: string) => {
    setEntities(prev => prev.filter(e => e.id !== entityId))
    setRelationships(prev =>
      prev.filter(r => r.sourceEntityId !== entityId && r.targetEntityId !== entityId)
    )
    setSelectedEntityId(null)
    toast.success('Entity deleted')
  }, [])

  const handleCreateRelationship = useCallback(
    (sourceEntityId: string, targetEntityId: string) => {
      const sourceEnt = entities.find(e => e.id === sourceEntityId)
      const targetEnt = entities.find(e => e.id === targetEntityId)
      if (!sourceEnt || !targetEnt) return

      const newRel: BuiltRelationship = {
        id: crypto.randomUUID(),
        relationshipType: '',
        sourceEntityId,
        targetEntityId,
        color: pickColor(relationships.length),
        columnMappings: {},
      }
      setRelationships(prev => [...prev, newRel])

      setSelectedRelationshipId(newRel.id)
      setSelectedEntityId(null)
      setRightPanelVisible(true)
    },
    [entities, relationships.length]
  )

  const handleUpdateRelationship = useCallback(
    (
      relationshipId: string,
      relationshipType: string,
      propertyMappings: Record<string, string>
    ) => {
      setRelationships(prev =>
        prev.map(rel =>
          rel.id === relationshipId
            ? { ...rel, relationshipType, columnMappings: propertyMappings }
            : rel
        )
      )
      toast.success('Relationship updated')
    },
    []
  )

  const handleDeleteRelationship = useCallback((relationshipId: string) => {
    setRelationships(prev => prev.filter(r => r.id !== relationshipId))
    setSelectedRelationshipId(null)
    toast.success('Relationship deleted')
  }, [])

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (selectedEntityId && selectedEntityId !== node.id && saveCurrentEntityRef.current) {
        const saved = saveCurrentEntityRef.current()
        if (!saved) return
      }
      setSelectedEntityId(node.id)
      setSelectedRelationshipId(null)
      setRightPanelVisible(true)
    },
    [selectedEntityId]
  )

  const handleEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      if (selectedEntityId && saveCurrentEntityRef.current) {
        const saved = saveCurrentEntityRef.current()
        if (!saved) return
      }
      setSelectedEntityId(null)
      setSelectedRelationshipId(edge.id)
      setRightPanelVisible(true)
    },
    [selectedEntityId]
  )

  const handlePropertyMapping = useCallback(
    (propertyName: string, columnName: string | null) => {
      if (!selectedEntityId) return
      setEntities(prev =>
        prev.map(e =>
          e.id === selectedEntityId
            ? {
                ...e,
                columnMappings: columnName
                  ? { ...e.columnMappings, [propertyName]: columnName }
                  : Object.fromEntries(
                      Object.entries(e.columnMappings).filter(([k]) => k !== propertyName)
                    ),
              }
            : e
        )
      )
    },
    [selectedEntityId]
  )

  const handleSetIdentifier = useCallback(
    (columnName: string | null) => {
      if (!selectedEntityId) return
      setEntities(prev =>
        prev.map(e => (e.id === selectedEntityId ? { ...e, identifierColumn: columnName } : e))
      )
    },
    [selectedEntityId]
  )

  const handleStartPickingColumn = useCallback(
    (propertyName: string) => {
      if (selectedEntityId) {
        setPickingContext({ type: 'property', propertyName })
        toast.info('Click a column header in the preview below')
      }
    },
    [selectedEntityId]
  )

  const handleStartPickingRowConditionColumn = useCallback(
    (clauseIndex: number) => {
      if (!selectedEntityId) return
      setPickingContext({ type: 'row-condition-column', entityId: selectedEntityId, clauseIndex })
      toast.info('Click a column header to use in this condition')
    },
    [selectedEntityId]
  )

  const handleStartPickingRowConditionValue = useCallback(
    (clauseIndex: number) => {
      if (!selectedEntityId) return
      setPickingContext({ type: 'row-condition-value', entityId: selectedEntityId, clauseIndex })
      toast.info('Click a cell to use its value in this condition')
    },
    [selectedEntityId]
  )

  const handleColumnPicked = useCallback(
    (columnName: string) => {
      if (!pickingContext) return

      if (pickingContext.type === 'property') {
        handlePropertyMapping(pickingContext.propertyName, columnName)
        if (updateLocalPropertyMappingRef.current) {
          updateLocalPropertyMappingRef.current(pickingContext.propertyName, columnName)
        }
        setPickingContext(null)
        toast.success(`Mapped to: ${columnName}`)
      } else if (pickingContext.type === 'row-condition-column') {
        const ctx = pickingContext
        setEntities(prev =>
          prev.map(e => {
            if (e.id !== ctx.entityId || !e.rowCondition) return e
            const updatedClauses = e.rowCondition.clauses.map((clause, i) =>
              i === ctx.clauseIndex ? { ...clause, column: columnName } : clause
            )
            return { ...e, rowCondition: { ...e.rowCondition, clauses: updatedClauses } }
          })
        )
        if (updateLocalRowConditionRef.current) {
          updateLocalRowConditionRef.current(pickingContext.clauseIndex, 'column', columnName)
        }
        setPickingContext(null)
        toast.success(`Column set to: ${columnName}`)
      }
    },
    [pickingContext, handlePropertyMapping]
  )

  const handleCellPicked = useCallback(
    (_columnName: string, cellValue: string | number | boolean | null) => {
      if (!pickingContext || pickingContext.type !== 'row-condition-value') return
      const ctx = pickingContext

      setEntities(prev =>
        prev.map(e => {
          if (e.id !== ctx.entityId || !e.rowCondition) return e
          const updatedClauses = e.rowCondition.clauses.map((clause, i) =>
            i === ctx.clauseIndex ? { ...clause, value: String(cellValue ?? '') } : clause
          )
          return { ...e, rowCondition: { ...e.rowCondition, clauses: updatedClauses } }
        })
      )

      if (updateLocalRowConditionRef.current) {
        updateLocalRowConditionRef.current(ctx.clauseIndex, 'value', String(cellValue ?? ''))
      }

      setPickingContext(null)
      toast.success(`Value set to: ${cellValue}`)
    },
    [pickingContext]
  )

  const handleCancelPicking = useCallback(() => {
    if (pickingContext) {
      setPickingContext(null)
      toast.info('Selection cancelled')
    }
  }, [pickingContext])

  // ESC cancels picking
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pickingContext) {
        handleCancelPicking()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [pickingContext, handleCancelPicking])

  // click-outside cancels picking
  useEffect(() => {
    if (!pickingContext) return
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (tableContainerRef.current && !tableContainerRef.current.contains(target)) {
        handleCancelPicking()
      }
    }
    window.document.addEventListener('mousedown', handleClickOutside, true)
    return () => window.document.removeEventListener('mousedown', handleClickOutside, true)
  }, [pickingContext, handleCancelPicking])

  // ---- query / filter ----
  const handleQuery = useCallback(
    (req: TabularQueryRequest) => {
      setCurrentQuery(req)
      setCurrentOffset(0)
      setIsLoading(true)
      setQueryError(null)
      lakeApi
        .queryTabular(document.id, { ...req, offset: 0, limit: 250 })
        .then(resp => {
          setColumns(resp.columns)
          setRows(resp.rows)
          setTotalRows(resp.total_rows)
        })
        .catch((err: unknown) => setQueryError(err instanceof Error ? err.message : 'Query failed'))
        .finally(() => setIsLoading(false))
    },
    [document.id]
  )

  const handlePrevPage = useCallback(() => {
    if (isNavigating || currentOffset === 0) return
    const newOffset = Math.max(0, currentOffset - 250)
    setIsNavigating(true)
    lakeApi
      .queryTabular(document.id, { ...currentQuery, offset: newOffset, limit: 250 })
      .then(resp => {
        setRows(resp.rows)
        setCurrentOffset(newOffset)
        setTotalRows(resp.total_rows)
      })
      .catch((err: unknown) =>
        toast.error(err instanceof Error ? err.message : 'Failed to load page')
      )
      .finally(() => setIsNavigating(false))
  }, [document.id, currentQuery, currentOffset, isNavigating])

  const handleNextPage = useCallback(() => {
    if (isNavigating) return
    const newOffset = currentOffset + 250
    setIsNavigating(true)
    lakeApi
      .queryTabular(document.id, { ...currentQuery, offset: newOffset, limit: 250 })
      .then(resp => {
        setRows(resp.rows)
        setCurrentOffset(newOffset)
        setTotalRows(resp.total_rows)
      })
      .catch((err: unknown) =>
        toast.error(err instanceof Error ? err.message : 'Failed to load page')
      )
      .finally(() => setIsNavigating(false))
  }, [document.id, currentQuery, currentOffset, isNavigating])

  // ---- row / column / drag selection ----
  const handleRowClick = useCallback(
    (rowIndex: number) => {
      if (readOnly) return
      if (selectionMode === 'select-header') {
        setHeaderRowIndex(rowIndex)
        if (headerSource === 'file') {
          const rowIndexColIdx = columns.findIndex(c => c.name === '__row_index')
          const targetRow = rows.find(r => Number(r[rowIndexColIdx]) === rowIndex)
          if (targetRow) {
            const dc = columns.filter(c => c.name !== '__row_index')
            const names = dc.map(col => {
              const idx = columns.indexOf(col)
              const val = targetRow[idx]
              return val != null ? String(val) : ''
            })
            setHeaderNames(names)
          }
        }
        setSelectionMode('build-batch')
      } else if (selectionMode === 'build-batch') {
        setPendingRows(prev => {
          const next = new Set(prev)
          if (next.has(rowIndex)) next.delete(rowIndex)
          else next.add(rowIndex)
          return next
        })
      }
    },
    [readOnly, selectionMode, headerSource, columns, rows]
  )

  const handleColumnClick = useCallback(
    (colIdx: number) => {
      if (readOnly) return
      if (selectionMode !== 'build-batch') return
      setPendingColumns(prev => {
        const next = new Set(prev)
        if (next.has(colIdx)) next.delete(colIdx)
        else next.add(colIdx)
        return next
      })
    },
    [readOnly, selectionMode]
  )

  const handleDragSelect = useCallback(
    (dragRows: number[], dragCols: number[]) => {
      if (readOnly) return
      if (selectionMode !== 'build-batch') return
      setPendingRows(new Set(dragRows))
      setPendingColumns(new Set(dragCols))
    },
    [readOnly, selectionMode]
  )

  const clearSelection = useCallback(() => {
    setPendingRows(new Set())
    setPendingColumns(new Set())
  }, [])

  // ---- validation (create mode) ----
  const emptyHeaders = headerNames.some(h => !h.trim())
  const noHeaders = headerNames.length === 0
  const noSelection = pendingRows.size === 0 || pendingColumns.size === 0
  const noCaseSelected = !selectedCaseId
  const canSave = !emptyHeaders && !noHeaders && !noSelection && !noCaseSelected && !saving

  // ---- save (create mode) ----
  const handleSave = useCallback(async () => {
    if (!canSave) return
    setSaving(true)
    try {
      // Persist the real row/column selection as a `tabular` scope — the case
      // extraction pipeline clips the source's Parquet to exactly this subset
      // at read time (the copied source file stays unchanged).
      const selectedColumns = [...pendingColumns]
        .sort((a, b) => a - b)
        .map(i => dataCols[i]?.name)
        .filter((name): name is string => Boolean(name))
      const selectedRows = [...pendingRows].sort((a, b) => a - b)

      await caseDocumentImportApi.upsert(document.id, {
        case_id: selectedCaseId,
        scope_type: 'tabular',
        selected_columns: selectedColumns,
        selected_rows: selectedRows,
        scope_note: scopeNote.trim() || null,
      })
      toast.success('Imported to case')
      onSaved()
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to save scope')
    } finally {
      setSaving(false)
    }
  }, [
    canSave,
    scopeNote,
    pendingRows,
    pendingColumns,
    dataCols,
    document.id,
    selectedCaseId,
    onSaved,
    onClose,
  ])

  return {
    columns,
    rows,
    totalRows,
    isLoading,
    isNavigating,
    currentOffset,
    queryError,
    dataCols,
    hasParquet,
    selectionMode,
    setSelectionMode,
    headerSource,
    setHeaderSource,
    headerRowIndex,
    setHeaderRowIndex,
    headerNames,
    setHeaderNames,
    pendingRows,
    pendingColumns,
    clearSelection,
    cases,
    selectedCaseId,
    setSelectedCaseId,
    scopeNote,
    setScopeNote,
    saving,
    canSave,
    noHeaders,
    emptyHeaders,
    noSelection,
    noCaseSelected,
    handleSave,
    readOnlyCaseName,
    scopeLoadError,
    loadedScope,
    setLoadedScope,
    isGeospatial,
    marking,
    handleSaveAndMarkReviewed,
    splitPercent,
    handleDragStart,
    sidebarWidth,
    handleSidebarDragStart,
    schema,
    loadingSchema,
    entities,
    relationships,
    selectedEntityId,
    setSelectedEntityId,
    selectedRelationshipId,
    rightPanelVisible,
    setRightPanelVisible,
    availableColumns,
    tabularData,
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    handleAddEntity,
    handleDeleteEntity,
    handleCreateRelationship,
    handleUpdateRelationship,
    handleDeleteRelationship,
    handleNodeClick,
    handleEdgeClick,
    handlePropertyMapping,
    handleSetIdentifier,
    handleAutoLayout,
    pickingContext,
    handleStartPickingColumn,
    handleStartPickingRowConditionColumn,
    handleStartPickingRowConditionValue,
    handleColumnPicked,
    handleCellPicked,
    saveCurrentEntityRef,
    updateLocalPropertyMappingRef,
    updateLocalRowConditionRef,
    tableContainerRef,
    handleQuery,
    handlePrevPage,
    handleNextPage,
    handleRowClick,
    handleColumnClick,
    handleDragSelect,
  }
}
